import { motion } from "framer-motion";
import { FolderKanban } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { formatError, useAuth } from "@/auth";
import { TodoMeta } from "@/components/projects/KanbanBoard";
import { TodoPanel } from "@/components/projects/TodoPanel";

const COLS = [["todo", "À faire"], ["doing", "En cours"], ["done", "Terminé (14 j)"]];
const DUES = [["", "Toutes échéances"], ["late", "En retard"], ["today", "Aujourd'hui"], ["week", "7 prochains jours"], ["none", "Sans échéance"]];
export const parisDay = (d = new Date()) => d.toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" });
const iso = parisDay;

const dueMatch = (t, due) => {
  const today = iso(new Date());
  const week = iso(new Date(Date.now() + 7 * 86400000));
  if (due === "late") return t.due_date && t.due_date < today && t.status !== "done";
  if (due === "today") return t.due_date === today;
  if (due === "week") return t.due_date && t.due_date >= today && t.due_date <= week;
  if (due === "none") return !t.due_date;
  return true;
};

function useStored(key, initial) {
  const [v, setV] = useState(() => localStorage.getItem(key) ?? initial);
  return [v, (x) => { setV(x); localStorage.setItem(key, x); }];
}

function Filters({ data, f, user }) {
  return (
    <div className="pj-toolbar" data-testid="tasks-filters">
      <select value={f.assignee} onChange={(e) => f.setAssignee(e.target.value)} data-testid="tasks-filter-assignee">
        <option value="">Tous les responsables</option>
        <option value={user.id}>Moi</option>
        <option value="none">Non assignées</option>
        {data.members.filter((m) => m.id !== user.id).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
      <select value={f.project} onChange={(e) => f.setProject(e.target.value)} data-testid="tasks-filter-project">
        <option value="">Tous les projets</option>
        {data.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select value={f.due} onChange={(e) => f.setDue(e.target.value)} data-testid="tasks-filter-due">
        {DUES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </div>
  );
}

export function AllTasksBoard() {
  const { user } = useAuth();
  const [data, setData] = useState({ projects: [], members: [], todos: [] });
  const [assignee, setAssignee] = useStored("tasks-assignee", user.id);
  const [project, setProject] = useStored("tasks-project", "");
  const [due, setDue] = useStored("tasks-due", "");
  const [over, setOver] = useState(null);
  const [current, setCurrent] = useState(null);
  const dragging = useRef(false);
  const load = useCallback(() => api.get("/tasks", { params: { assignee, project_id: project } }).then(({ data: d }) => setData(d)).catch(() => {}), [assignee, project]);
  useEffect(() => { load(); }, [load]);
  const move = async (t, status) => {
    setData((d) => ({ ...d, todos: d.todos.map((x) => (x.id === t.id ? { ...x, status } : x)) }));
    try { await api.patch(`/projects/${t.project_id}/todos/${t.id}`, { status }); window.dispatchEvent(new Event("tasks-changed")); } catch (e) { toast.error(formatError(e.response?.data?.detail)); load(); }
  };
  const open = async (t) => {
    try {
      const [{ data: p }, { data: d }] = await Promise.all([api.get(`/projects/${t.project_id}`), api.get(`/projects/${t.project_id}/todos`)]);
      setCurrent({ project: p, lists: d.lists, todo: d.todos.find((x) => x.id === t.id) || t });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const colAt = (e) => { const p = e.changedTouches?.[0] || e; return document.elementsFromPoint(p.clientX, p.clientY).find((el) => el.dataset?.col)?.dataset.col || null; };
  const endDrag = (t) => (e) => { const s = colAt(e); setOver(null); setTimeout(() => { dragging.current = false; }, 0); if (s && s !== t.status) move(t, s); };
  const shown = data.todos.filter((t) => dueMatch(t, due));
  return (
    <div data-testid="all-tasks-board">
      <Filters data={data} user={user} f={{ assignee, setAssignee, project, setProject, due, setDue }} />
      <div className="pj-kanban">
        {COLS.map(([status, label]) => {
          const cards = shown.filter((t) => t.status === status);
          return (
            <section key={status} data-col={status} className={`pj-col pj-col-${status} ${over === status ? "is-over" : ""}`} data-testid={`tasks-col-${status}`}>
              <header><span className="pj-col-title">{label}</span><span className="pj-count" data-testid={`tasks-col-count-${status}`}>{cards.length}</span></header>
              {cards.length === 0 && <p className="pj-col-empty">Aucune tâche</p>}
              {cards.map((t) => (
                <motion.article key={t.id} drag dragSnapToOrigin dragMomentum={false} whileDrag={{ scale: 1.04, rotate: 2, zIndex: 60 }} onDragStart={() => { dragging.current = true; }} onDrag={(e) => setOver(colAt(e))} onDragEnd={endDrag(t)} onClick={() => !dragging.current && open(t)} className="pj-card" data-testid={`tasks-card-${t.id}`}>
                  <small className="pj-card-list"><FolderKanban size={11} /> {t.project_name}{t.list_name && ` · ${t.list_name}`}</small>
                  <p>{t.title}</p>
                  <TodoMeta todo={t} members={data.members} />
                </motion.article>
              ))}
            </section>
          );
        })}
      </div>
      {current && <TodoPanel key={current.todo.id} project={current.project} todo={current.todo} lists={current.lists} members={current.project.members_info} onClose={() => setCurrent(null)} onSaved={() => { load(); window.dispatchEvent(new Event("tasks-changed")); }} />}
    </div>
  );
}
